import { useContext } from 'react';

import { css } from '@emotion/react';
import { Share as ShareIcon, Download } from '@mui/icons-material';
import { Skeleton, Box, Typography, Theme, IconButton } from '@mui/material';
import { AmplifyServiceContext, Types } from '@scorenco/core';
import {
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  differenceInSeconds,
} from 'date-fns';

import { ClubLogo } from '../../../components';
import {
  Item,
  ItemImage,
  ItemContent,
  ItemProps,
  Image,
} from '../../../primitives';
import { vars } from '../../../style';

import { EventPostActionItemLivePlayer } from './_internal/EventPostActionItemLivePlayer';
import { EventPostActionItemSubstitution } from './_internal/EventPostActionItemSubstitution';
import { EventPostActionItemMenu } from './EventPostActionItemMenu';

type EventData = {
  live_team?: unknown;
  main_score_1?: unknown;
  main_score_2?: unknown;
  shootout_1?: unknown;
  shootout_2?: unknown;
  inner_score_1?: unknown;
  inner_score_2?: unknown;
  minute?: unknown;
  live_player?: unknown;
  incoming_player?: unknown;
  outgoing_player?: unknown;
  player_in?: unknown;
  player_out?: unknown;
  set_nb?: unknown;
  remaining_minute?: unknown;
  remaining_second?: unknown;
  end?: unknown;
};

type PlayingTeam = {
  id?: number;
  score?: unknown;
  logo?: string;
  name?: string;
  name_in_competition?: string;
  role?: string;
  club_id?: number;
};

const cs = {
  logo: css`
    width: 100%;
    height: 100%;
    padding: 0.25rem;
  `,
  content: css`
    display: flex;
    /* border-radius: ${'0.25rem'}; */
    overflow: hidden;
    margin: 1rem 0;
    padding: 0.5rem 1rem;
    color: var(${vars.color.secondary100});
    align-items: center;
    justify-content: center;
  `,
  action: (theme: Theme) => css`
    text-transform: uppercase;
    font-weight: bold;
    font-family: ${theme.typography.fontFamilyAlt};
    margin: 0;
    line-height: 1.2;
    display: flex;
    align-items: center;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
  `,
  score: (theme: Theme) => css`
    display: flex;
    justify-content: center;
    align-items: center;
    background: ${theme.vars.palette.background.paper};
    margin: 0 0.125rem;
    font-weight: bold;
    width: 1.5rem;
    font-family: ${theme.typography.fontFamilyAlt};
    height: 2rem;
    color: var(${vars.color.secondary500});
    text-align: center;
    border-radius: 3px;
  `,
};

function formatCustomDistanceToNow(date: Date | string): string {
  const now = new Date();
  const dateObj: Date = typeof date === 'string' ? new Date(date) : date;
  const seconds = differenceInSeconds(now, dateObj);
  const minutes = differenceInMinutes(now, dateObj);
  const hours = differenceInHours(now, dateObj);
  let days = differenceInDays(now, dateObj);

  const partialDay = seconds % (24 * 60 * 60);

  if (partialDay > 0) {
    days += 1;
  }
  if (seconds < 60) {
    return `${seconds}s`;
  } else if (minutes < 60) {
    return `${minutes}m`;
  } else if (hours < 24) {
    return `${hours}h`;
  } else {
    return `${days}j`;
  }
}

export const EventPostActionItem = ({
  eventPost,
  event,
  live,
  ...rest
}: EventPostActionItemProps) => {
  // Pas de menu sans authentification (ex. widgets)
  const hasAmplify = !!useContext(AmplifyServiceContext);
  const postData = eventPost?.data as EventData | null | undefined;
  const eventTeams = event?.teams as PlayingTeam[] | undefined;
  const liveTeam = postData?.live_team;

  const homeLiveTeam = live?.teams?.find((team) => {
    return team.id === Number(liveTeam);
  });

  const teamInSeason = eventTeams?.find((team: PlayingTeam) => {
    return team.id === homeLiveTeam?.playing_team_id;
  });
  const hasScore =
    Number.isInteger(postData?.main_score_1) &&
    Number.isInteger(postData?.main_score_2);
  const hasShootOut =
    Number.isInteger(postData?.shootout_1) &&
    Number.isInteger(postData?.shootout_2);
  const hasInnerScore =
    Number.isInteger(postData?.inner_score_1) &&
    Number.isInteger(postData?.inner_score_2);

  const isHome = homeLiveTeam?.role === 'home';

  const downloadImage = async (imageSrc: string, imageName?: string) => {
    try {
      const image = await fetch(imageSrc);
      const imageBlob = await image.blob();
      const imageURL = URL.createObjectURL(imageBlob);

      const link = document.createElement('a');
      link.href = imageURL;
      link.download = imageName || 'image'; // Nom de fichier par défaut
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Erreur lors du téléchargement de l'image : ", error);
    }
  };

  return (
    <Item {...rest}>
      <ItemImage
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
        }}
      >
        <Image
          css={css`
            user-select: none;
            width: 100%;
            height: 100%;
            display: inline-block;
            flex-shrink: 0;
            stroke-width: 1.5;
            stroke-linecap: round;
            stroke-linejoin: round;
            fill-rule: evenodd;
          `}
          src={eventPost?.action?.icon}
        />
        {!!postData?.minute && (
          <Typography variant="body2" component="span">
            {`${postData?.minute}'`}
          </Typography>
        )}
      </ItemImage>
      <ItemContent>
        {liveTeam ? (
          <Box>
            <Box
              css={cs.content}
              sx={[
                {
                  borderRadius:
                    postData?.live_player ||
                    postData?.incoming_player ||
                    postData?.outgoing_player
                      ? '0.25rem 0.25rem 0 0'
                      : '0.25rem',
                  maxWidth: '100%',
                  minWidth: '11.7rem',
                  fontSize: { xs: '0.8rem', sm: '1rem', md: '1rem' },
                },
                isHome
                  ? {
                      backgroundColor: 'primary.main',
                    }
                  : {
                      backgroundColor: 'secondary.main',
                    },
              ]}
            >
              <ClubLogo
                css={css`
                  width: 2rem;
                  height: 100%;
                `}
                logo={teamInSeason?.logo}
                name={teamInSeason?.name_in_competition}
                shieldColor={teamInSeason?.role as 'home' | 'away' | undefined}
                width={32}
                height={32}
              />
              <Box
                sx={{
                  flex: 1,
                  marginLeft: { xs: '0.4rem', sm: '1rem', md: '1rem' },
                  fontSize: { xs: '0.8rem', sm: '1rem', md: '1rem' },
                }}
              >
                <Typography
                  sx={{ fontSize: { xs: '0.8rem', sm: '1rem', md: '1rem' } }}
                  css={cs.action}
                >
                  {eventPost?.action?.name}
                </Typography>
                <div
                  css={css`
                    line-height: 1.2;
                  `}
                >
                  {teamInSeason?.name_in_competition || <Skeleton />}
                </div>
              </Box>

              {(hasScore || hasInnerScore) && (
                <Box
                  sx={{
                    fontSize: { xs: '0.8rem', sm: '1rem', md: '1rem' },
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row', md: 'row' },
                    gap: '0.2rem',
                  }}
                >
                  <Box
                    sx={{ fontSize: { xs: '0.8rem', sm: '1rem', md: '1rem' } }}
                    css={cs.score}
                  >
                    {
                      (hasInnerScore
                        ? postData?.inner_score_1
                        : postData?.main_score_1) as number | null | undefined
                    }
                  </Box>
                  <Box
                    sx={{ fontSize: { xs: '0.8rem', sm: '1rem', md: '1rem' } }}
                    css={cs.score}
                  >
                    {
                      (hasInnerScore
                        ? postData?.inner_score_2
                        : postData?.main_score_2) as number | null | undefined
                    }
                  </Box>
                </Box>
              )}
              {hasShootOut && (
                <div
                  css={css`
                    display: flex;
                  `}
                >
                  <Box css={cs.score}>
                    {postData?.shootout_1 as number | null | undefined}
                  </Box>
                  <Box css={cs.score}>
                    {postData?.shootout_2 as number | null | undefined}
                  </Box>
                </div>
              )}
            </Box>
            {!!postData?.live_player && (
              <EventPostActionItemLivePlayer
                livePlayer={postData?.live_player}
                home={isHome}
              />
            )}
            {!!(postData?.player_in || postData?.player_out) && (
              <EventPostActionItemSubstitution
                liveIncomingPlayer={postData?.player_in}
                liveOutgoingPlayer={postData?.player_out}
                home={isHome}
              />
            )}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
              gap: 1,
              fontSize: { xs: '0.8rem', sm: '1rem', md: '1rem' },
              maxWidth: '100%',
            }}
          >
            {eventPost?.action?.action_type === 'end_game' && (
              <Image
                src={event?.event?.share_image_url}
                sx={{ marginTop: '1rem', width: '30rem' }}
              />
            )}
            <Box
              css={cs.content}
              sx={[
                {
                  width: '100%',
                  backgroundColor: 'primary.main',
                  borderRadius: '0.25rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                },
                eventPost?.action?.action_type === 'end_game'
                  ? {
                      marginTop: '0 !important',
                    }
                  : {
                      marginTop: '1rem',
                    },
              ]}
            >
              <p css={cs.action}>
                {[
                  eventPost?.action?.name,
                  postData?.set_nb && `set n°${postData?.set_nb}`,
                  hasScore &&
                    `(${postData?.main_score_1}-${postData?.main_score_2})`,
                  hasShootOut &&
                    `(${postData?.shootout_1}-${postData?.shootout_2})`,
                  // hasInnerScore &&
                  //   `(${postData?.inner_score_1}-${postData?.inner_score_2})`,
                ]
                  .filter(Boolean)
                  .join(' ')}
                {hasInnerScore && (
                  <div
                    css={css`
                      display: flex;
                    `}
                  >
                    <Box
                      sx={{
                        fontSize: { xs: '0.8rem', sm: '1rem', md: '1rem' },
                      }}
                      css={cs.score}
                    >
                      {postData?.inner_score_1 as number | null | undefined}
                    </Box>
                    <Box
                      sx={{
                        fontSize: { xs: '0.8rem', sm: '1rem', md: '1rem' },
                      }}
                      css={cs.score}
                    >
                      {postData?.inner_score_2 as number | null | undefined}
                    </Box>
                  </div>
                )}
                {eventPost?.action?.action_type === 'end_game' && (
                  <>
                    <Box>
                      <IconButton
                        css={css`
                          color: currentColor;
                        `}
                        size="small"
                        onClick={async () => {
                          try {
                            if (typeof navigator.share !== 'undefined') {
                              await navigator.share({
                                url: `${process.env.NEXT_PUBLIC_WEB_URL}${event?.url}`,
                              });
                            } else {
                              throw new Error('Share API not available');
                            }
                          } catch (err) {
                            if (
                              err instanceof DOMException &&
                              err.name === 'AbortError'
                            ) {
                              return;
                            }
                            navigator.clipboard.writeText(
                              `${process.env.NEXT_PUBLIC_WEB_URL}${event?.url}`
                            );
                            console.log('error : ', err);
                          }
                        }}
                      >
                        <ShareIcon />
                      </IconButton>
                      <a>
                        <IconButton
                          css={css`
                            color: currentColor;
                          `}
                          size="small"
                          onClick={() => {
                            if (event?.event?.share_image_url) {
                              downloadImage(
                                event.event.share_image_url,
                                eventTeams?.[0]?.name_in_competition +
                                  ' - ' +
                                  eventTeams?.[1]?.name_in_competition +
                                  '.png'
                              );
                            }
                          }}
                        >
                          <Download />
                        </IconButton>
                      </a>
                    </Box>
                  </>
                )}
              </p>
              {!!(postData?.remaining_minute || postData?.remaining_second) && (
                <div>
                  {[
                    postData?.remaining_minute &&
                      (postData.remaining_minute as number) + ' minutes',
                    postData?.remaining_second &&
                      (postData.remaining_second as number) + ' secondes',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                </div>
              )}
            </Box>
          </Box>
        )}
      </ItemContent>
      <Box
        sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}
      >
        {!!eventPost?._created && (
          <Box
            css={css`
              padding: 0;

              color: #888;
              font-size: 0.8rem;
            `}
          >
            {formatCustomDistanceToNow(
              new Date(
                typeof eventPost?._created === 'string'
                  ? eventPost._created
                  : ''
              )
            )}
          </Box>
        )}
        {hasAmplify && eventPost?.id && eventPost?.__typename && (
          <EventPostActionItemMenu
            eventId={event?.id}
            post={eventPost as Types.Lives_Post}
            live={live}
            event={event}
            css={css`
              width: 2rem !important;
              height: 2rem !important;
              margin-left: 0.5rem;
            `}
          />
        )}
      </Box>
    </Item>
  );
};

export type EventPostActionItemProps = ItemProps & {
  eventPost?: Partial<Types.Lives_Post>;
  event?: Types.Competitions_Event_Detail;
  live?: Types.Lives_Live;
};
